import React, { useEffect, useState } from "react";
import { Button, Select, message } from "antd";
import {
  getChannelSettings,
  getContactModes,
  updateContactMode,
} from "../../services/polly/llmSettingsService";
import { CHANNEL_LLM_MODE_OPTIONS } from "../../constants/Polly/ChannelLlmModes";
import {
  CONTACT_LLM_MODE_OPTIONS,
  DEFAULT_CONTACT_LLM_MODE,
} from "../../constants/Polly/ContactLlmModes";

const { Option } = Select;

const channelModeLabel = (mode) =>
  CHANNEL_LLM_MODE_OPTIONS.find((opt) => opt.value === mode)?.label || mode;

// The page takes the id from router state, which can arrive as a string, while the mode list
// carries numbers. Comparing and sending a number keeps a stored mode from reading as the default.
const toId = (value) => Number(value);

const ContactPollyModesCard = ({ contactId, firstName, isActive, readOnly }) => {
  const [channels, setChannels] = useState([]);
  const [saved, setSaved] = useState({});
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // The mode list keeps only customers whose active flag is true, so for any other value
    // (including not yet loaded) a stored mode could not be shown.
    if (isActive !== true || contactId == null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const channelRows = await getChannelSettings();
        const modeLists = await Promise.all(
          channelRows.map((channel) => getContactModes(channel.channelType))
        );
        const modes = {};
        channelRows.forEach((channel, i) => {
          const row = modeLists[i].find((r) => toId(r.contactId) === toId(contactId));
          modes[channel.channelType] = row ? row.llmMode : DEFAULT_CONTACT_LLM_MODE;
        });
        if (cancelled) return;
        setChannels(channelRows);
        setSaved(modes);
        setDraft(modes);
      } catch (err) {
        if (!cancelled) message.error("Failed to load Polly settings");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contactId, isActive]);

  if (isActive !== true || channels.length === 0) return null;

  const changed = channels.filter((c) => draft[c.channelType] !== saved[c.channelType]);

  const onSave = async () => {
    if (saving || changed.length === 0) return;
    setSaving(true);
    const nextSaved = { ...saved };
    let failure = null;
    for (const channel of changed) {
      try {
        await updateContactMode(toId(contactId), channel.channelType, draft[channel.channelType]);
        nextSaved[channel.channelType] = draft[channel.channelType];
      } catch (err) {
        failure = failure || err;
      }
    }
    setSaved(nextSaved);
    setSaving(false);
    if (failure) {
      message.error(failure?.response?.data?.error || "Failed to save Polly settings");
    } else {
      message.success("Polly settings saved");
    }
  };

  const name = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : "";

  return (
    <div className="contact-attributes-container polly-modes-card">
      <div className="attributes-title-container">
        <p className="title">{`${name}’s Polly conversations`}</p>
        <Button
          className="standard-button secondary-orange-button"
          onClick={onSave}
          loading={saving}
          disabled={readOnly || saving || changed.length === 0}
        >
          Save
        </Button>
      </div>
      <p className="polly-modes-help">
        Choose whether Polly replies to this customer on each channel. Channel defaults are set in
        Polly › Settings.
      </p>
      {channels.map((channel) => (
        <div className="polly-modes-row" key={channel.id}>
          <span className="polly-modes-channel">{channel.displayName}</span>
          <Select
            value={draft[channel.channelType]}
            onChange={(value) => setDraft({ ...draft, [channel.channelType]: value })}
            disabled={readOnly || saving}
            style={{ width: 220 }}
          >
            {CONTACT_LLM_MODE_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>
          <span className="polly-modes-help">
            {`Channel default: ${channelModeLabel(channel.llmMode)}`}
          </span>
        </div>
      ))}
    </div>
  );
};

export default ContactPollyModesCard;
